import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {clientRoles} from "../../../user/user-roles-actions";
import {UserSubscription, UserSubscriptionType} from "../../../user/user-subscription";
import {
  InactiveWebsiteTroubleshootComponent
} from "../../inactive-webiste-troubleshoot/inactive-website-troubleshoot.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Utils} from "../../../shared/utils/utils";
import {NotifierComponent} from "../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../shared/components/notifier/notification";
import {NotificationType} from "../../../shared/components/notifier/notification-type";
import {WebsiteService} from "../../website.service";
import {Product} from "../../website/product";
import {UnavailableDialogComponent} from "../../website/plugins/plugin-download-dialog/unavailable-dialog.component";
import {TextUtils} from "../../../shared/utils/text-utils";
import {Config} from "../../../config/config";
import {Store} from "@ngrx/store";
import {DbStoreState} from "../../../store";
import * as fromStore from "../../../store";
import {SortByPipe} from "../../../shared/pipes/sort-by.pipe";
import {DbModalService} from "../../../modules/modal/modal";
import {
  BoosterTroubleInstructionsComponent
} from "../../website/booster/dialogs/booster-trouble-instructions/booster-trouble-instructions.component";
import {combineLatest, Subject} from "rxjs";
import {CreateWebsiteService} from "../../../services/local/create-website.service";
import {Router} from "@angular/router";
import {Website} from "../../website/website";
import {AwsUploadService} from "../../../shared/services/aws-upload/aws-upload.service";
import {ActivityAction, ActivityStatus} from "../../../activities/activity";
import {User} from "../../../user/user";
import {AccountUpgradedDialogComponent} from "../../../shared/components/dialogs/account-upgraded-dialog/account-upgraded-dialog.component";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";

@Component({
    selector: 'db-website-item-overlay',
    templateUrl: './website-item-overlay.component.html',
    styleUrls: ['./website-item-overlay.component.scss'],
    standalone: false
})
export class WebsiteItemOverlayComponent implements OnInit {

  @Input() websiteId: number;
  @Input() website?: Website;
  @Input() user: User;
  @Input() userSubscription: UserSubscription;
  @Input() subscriptionType: string;
  @Input() role: number;
  @Input() isHosted: boolean = false;
  @Input() isNetwork: boolean = false;
  @Input() isActive: boolean = false;
  @Input() isLocalHost: boolean = false;
  @Input() disableMigrateButton: boolean = false;
  @Input() hasCopy: boolean = false;
  @Input() isBooster: boolean = false;
  @Input() isIO: boolean = false;
  @Input() cdnIsEnabled: boolean;
  @Input() failedMigration: boolean = false;
  @Input() migrationInProgress: boolean = false;
  @Input() ongoingMigrationActivity: any;
  @Output() onMigrate: EventEmitter<void> = new EventEmitter<void>();
  @Output() onRefresh: EventEmitter<void> = new EventEmitter<void>();


  public isUploading: boolean = false;
  public migrateWithZipClicked: boolean = false;
  public loading: boolean = false;
  public domainId: number;
  demoLoading: boolean = false;
  hostedLoading: boolean = false;
  private pageId = '';
  private websiteUrl = '';

  public readonly clientRoles = clientRoles;
  public readonly userSubscriptionType = UserSubscriptionType;
  private _managerPlugin: Product;
  private _boosterPlugin: Product;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();


  constructor(
    private _dialogService: DialogService,
    private _websiteService: WebsiteService,
    private _store: Store<DbStoreState>,
    private _sortPipe: SortByPipe,
    private _modalService: DbModalService,
    private _createWebsiteService: CreateWebsiteService,
    private _router: Router,
    private _awsUploadService: AwsUploadService,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  ngOnInit() {
    this._store.dispatch(new fromStore.LoadTroubleShootData({websiteId: this.website.id, websiteUrl: this.website.site_url}));
    this._getBoosterAndManagerPlugins(this.websiteId);
    this._awsIsUploading();
    this._getOngoingMigrationActivity();
    if (this.user?.platformClient && this.website?.isAiBuilderDemo) {
      this._getRecreateOngoingActivity();
    }
  }

  public redirect(): void {
    this._router.navigateByUrl(`/websites/${this.websiteId}/booster/cloudflare`);
  }

  public troubleshootInactiveWebsite(): void {
    this._dialogService.addDialog(InactiveWebsiteTroubleshootComponent);
  }

  public showUploadWithZipDialog(): void {
    this.migrateWithZipClicked = true;
    this._createWebsiteService.openDownloadManagerMigrateModal(this.userSubscription && this.userSubscription.boosterProClient, true);
  }

  private _getOngoingMigrationActivity(): void {
    this._store.select(fromStore.getMigrationOngoingActivity)
      .subscribe(activity => {
        if (this.migrateWithZipClicked) {
          this.migrationInProgress = !!activity && activity.status === ActivityStatus.RUNNING;
        }
      });
  }

  private _awsIsUploading(): void {
    this._awsUploadService.isUploading.subscribe(loading => {
      if (!!loading && this.migrateWithZipClicked) {
        this.isUploading = true;
      }
    });
  }

  private _getBoosterAndManagerPlugins(websiteId): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(websiteId))
      .pipe(
        first(loaded => loaded, null),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteOptimizerPlugin(websiteId)),
          this._store.select(fromStore.getWebsiteManagerPlugin(websiteId))
        )),
        first()
      )
      .subscribe(([boosterPlugin, managerPlugin]) => {
        this._boosterPlugin = boosterPlugin;
        this._managerPlugin = managerPlugin;
      });
  }


  public showBoosterPluginInstructions(): void {
    if (!!this.isIO && !this._boosterPlugin) {
      const modal = this._modalService.open(BoosterTroubleInstructionsComponent, {
        height: 325, width: 700
      });
      modal.componentInstance.isPaid = this.subscriptionType !== UserSubscriptionType.FREE;
      modal.componentInstance.website = this.website;
      modal.componentInstance.isIO = this.isIO;
    }
    else if ((this._boosterPlugin || this._boosterPlugin && !this._boosterPlugin.active && !this.isIO) && this._managerPlugin && this._managerPlugin.active) {
      this._createWebsiteService.openMigrateWebsiteModal(this.websiteId, this.website.site_url);
    }
    else {
      const modal = this._modalService.open(BoosterTroubleInstructionsComponent, {
        height: 325, width: 700
      });
      modal.componentInstance.isPaid = this.subscriptionType !== UserSubscriptionType.FREE;
      modal.componentInstance.website = this.website;
    }
  }

  public refreshWebsiteConnection(): void {
    if (this.loading) { return; }

    this.loading = true;
    this._websiteService.checkConnectionAvailability(this.websiteId)
      .pipe(
        filter(response => !!response),
        first()
      )
      .subscribe(response => {
        this.loading = false;
        // this._getAvailable10WebPlugins();

        if (response.success) {
          if (Utils.isTruthy(response.data.is_available)) {
            this.onRefresh.emit();
          } else {
            this._getAvailable10WebPlugins();
          }
        } else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }

  private _getAvailable10WebPlugins(): void {
    this._store.select(fromStore.getTenWebPluginsAndExtensions(this.websiteId))
      .pipe(
        first()
      )
      .subscribe((plugins: Product[]) => {
          this._unavailableDialog(this._filter10WebPlugins(plugins));
      });
  }

  private _filter10WebPlugins(plugins: Product[]): Product[] | null {
    if (!plugins && !plugins.length) {
      return null;
    }
    plugins = plugins.filter(plugin => !!plugin.hasPaidVersion);
    const sortedProducts: Product[] = <Product[]>this._sortPipe.transform(plugins, 'popularRank', 'asc');

    return sortedProducts.reduce((filtered: Product[], plugin) => {
      if (plugin.slug !== Config.optimizerPluginSlug) {
        if (plugin.description.length > 60) {
          const shortDesc = plugin.description.substr(0, 60);
          plugin.description = shortDesc.substr(0, Math.min(shortDesc.length, shortDesc.lastIndexOf(" "))) + '...';
        }
        return [...filtered, plugin];
      }
      return filtered;
    }, []);
  }

  private _unavailableDialog(plugins): void {
    this._dialogService
      .addDialog(UnavailableDialogComponent, {
        header: TextUtils.getText('text_unavailablePluginDownload.bundleTitle'),
        plugins: plugins,
        fromLocal: null,
        userSubscription: this.subscriptionType
      });
  }

  public setupFlow(): void {
    this._dialogService.addDialog(AccountUpgradedDialogComponent, {
      currentSubscription: this.userSubscription,
      upgradedFromTrial: true,
      showSetupFlow: true
    });
  }

  private _getRecreateOngoingActivity(): void {
    this._store
      .select(fromStore.getOngoingActivity)
      .pipe(
        takeUntil(this._destroyStream$),
        filter((activity) =>
          activity &&
          (activity.action === ActivityAction.CREATE_WEBSITE || activity.action === ActivityAction.INSTALL ||
            activity.action === ActivityAction.INSTALL_HOSTED_TEMPLATE)
        )
      )
      .subscribe((activity) => {

        this.demoLoading = true;
        this.hostedLoading = activity && activity.action === ActivityAction.CREATE_WEBSITE && activity.status === ActivityStatus.RUNNING;

        const hostedDone = activity && activity.action === ActivityAction.INSTALL_HOSTED_TEMPLATE && (activity.status === ActivityStatus.DONE || activity.status === ActivityStatus.FAILED);
        if (hostedDone) {
          this._store.dispatch(new fromStore.LoadUser());
        }
      });
  }



}
